import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Star, MapPin, ChevronLeft, ChevronRight } from "lucide-react";
import { apiUrl, fetchJson } from "@/lib/api";

/* ================================================================== */
/*  Social proof — REAL profiles and posted availabilities pulled from */
/*  the public API, shown as an auto-advancing carousel (3 at a time). */
/*  Segmenting into a rotating carousel reads as more activity than a   */
/*  full list. Stays quiet while loading and hides entirely if there    */
/*  isn't enough real activity to look credible.                        */
/* ================================================================== */

const GRADS = [
  "linear-gradient(145deg,#b08968,#7f5539)",
  "linear-gradient(145deg,#7fa6a0,#4a7c74)",
  "linear-gradient(145deg,#c9a24c,#9a7b2e)",
  "linear-gradient(145deg,#9fb2d8,#3a4a6a)",
  "linear-gradient(145deg,#cdb6e6,#674c97)",
];

function initials(name) {
  return (name || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
}

function ProfileCard({ c, grad }) {
  const isOrg = c.type === "org";
  const rating = c.rating || {};
  const hasRating = rating.count > 0 && rating.average != null;
  const verified = (c.verified_credentials || []).length > 0;
  const accent = c.accent_color || null;
  const avatarStyle = accent
    ? { backgroundColor: accent, backgroundImage: `linear-gradient(145deg, ${accent}, color-mix(in srgb, ${accent} 62%, #000))` }
    : { background: grad };
  return (
    <Link to={c.url || "/browse?show=service"} className="lsp-card" data-card>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <span className="lsp-mono" style={{ fontSize: "0.5625rem", letterSpacing: ".14em", padding: "3px 9px", borderRadius: "var(--r-pill)", ...(isOrg ? { background: "var(--brand-tint)", color: "var(--brand-ink)" } : { background: "#EAEEF6", color: "#3A4A6A" }) }}>
          {isOrg ? "Business" : "Pro"}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {verified && <ShieldCheck size={14} strokeWidth={2} color="var(--brand)" aria-label="Verified" />}
          {hasRating && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "0.8125rem", color: "var(--ink)" }}>
              <Star size={13} fill="var(--star)" stroke="var(--star)" aria-hidden="true" /> {Number(rating.average).toFixed(1)}
            </span>
          )}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 12 }}>
        {c.image_url ? (
          <img src={c.image_url} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover", border: "2px solid rgba(255,255,255,.8)", flex: "0 0 auto" }} />
        ) : (
          <span style={{ width: 42, height: 42, borderRadius: "50%", display: "grid", placeItems: "center", color: "#fff", fontWeight: 700, fontSize: "0.875rem", flex: "0 0 auto", ...avatarStyle }}>
            {initials(c.name)}
          </span>
        )}
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontWeight: 600, fontSize: "0.9375rem", color: "var(--ink)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
          {(c.vertical || c.location) && (
            <span className="lsp-mono" style={{ display: "block", fontSize: "0.5625rem", letterSpacing: ".12em", color: "var(--text-muted)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {[c.vertical, c.location].filter(Boolean).join(" · ")}
            </span>
          )}
        </span>
      </div>
      {c.tagline && (
        <p style={{ fontSize: "0.78125rem", lineHeight: 1.5, color: "var(--text-secondary)", margin: "10px 0 0", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{c.tagline}</p>
      )}
      <div style={{ marginTop: "auto", paddingTop: 10 }}>
        {typeof c.sift_score === "number" && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "var(--brand-tint)", borderRadius: "var(--r-pill)", padding: "3px 9px" }}>
            <Star size={11} strokeWidth={2} color="var(--brand)" aria-hidden="true" />
            <span className="lsp-mono" style={{ fontSize: "0.5625rem", letterSpacing: ".1em", color: "var(--brand-ink)" }}>Sift Score {c.sift_score}</span>
          </span>
        )}
      </div>
    </Link>
  );
}

function PostCard({ p }) {
  const isNeed = p.poster_type === "org";
  return (
    <Link to={p.id != null ? `/postings/${p.id}` : "/browse"} className="lsp-card" data-card>
      <span className="lsp-mono" style={{ alignSelf: "flex-start", fontSize: "0.5625rem", letterSpacing: ".14em", padding: "3px 9px", borderRadius: "var(--r-pill)", ...(isNeed ? { background: "var(--brand-tint)", color: "var(--brand-ink)" } : { background: "#EAEEF6", color: "#3A4A6A" }) }}>
        {isNeed ? "Hiring" : "Available"}
      </span>
      <div style={{ fontWeight: 600, fontSize: "0.90625rem", color: "var(--ink)", margin: "12px 0 6px", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{p.title}</div>
      <div style={{ marginTop: "auto", fontSize: "0.78125rem", color: "var(--text-tertiary)", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {p.location && <MapPin size={13} strokeWidth={1.7} aria-hidden="true" />}
        {[p.poster_name, p.location].filter(Boolean).join(" · ") || "On Sift"}
      </div>
    </Link>
  );
}

export default function LandingSocialProof() {
  const [profiles, setProfiles] = useState(null);
  const [posts, setPosts] = useState(null);
  const [paused, setPaused] = useState(false);
  const trackRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchJson(apiUrl("/api/accounts/public/services/?type=both&limit=8"))
      .then((b) => { if (alive) setProfiles(Array.isArray(b?.results) ? b.results : []); })
      .catch(() => { if (alive) setProfiles([]); });
    fetchJson(apiUrl("/api/accounts/public/postings/?limit=8"))
      .then((b) => { if (alive) setPosts(Array.isArray(b?.results) ? b.results : []); })
      .catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, []);

  // Auto-advance one card at a time; loop back to the start at the end.
  useEffect(() => {
    if (paused) return;
    const el = trackRef.current;
    if (!el) return;
    const id = setInterval(() => {
      const first = el.querySelector("[data-card]");
      const step = first ? first.getBoundingClientRect().width + 14 : el.clientWidth / 3;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 6;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + step, behavior: "smooth" });
    }, 3200);
    return () => clearInterval(id);
  }, [paused, profiles, posts]);

  if (profiles === null || posts === null) return null;
  if (profiles.length + posts.length < 4) return null;

  // Profiles first, then posted availabilities. Uniform card height keeps the
  // row clean even where the two card types meet.
  const cards = [
    ...profiles.map((c, i) => <ProfileCard key={`pf-${c.slug || i}`} c={c} grad={GRADS[i % GRADS.length]} />),
    ...posts.map((p, i) => <PostCard key={`po-${p.id ?? i}`} p={p} />),
  ];

  const nudge = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    setPaused(true);
    el.scrollBy({ left: dir * el.clientWidth * 0.82, behavior: "smooth" });
  };

  return (
    <section className="lsp" style={{ maxWidth: 1080, margin: "0 auto", padding: "16px 24px 8px", textAlign: "center" }}>
      <style>{`
        .lsp-mono { font-family:var(--font-mono); text-transform:uppercase; }
        .lsp-track { display:flex; gap:14px; overflow-x:auto; scroll-snap-type:x mandatory; scrollbar-width:none; -ms-overflow-style:none; padding:6px 2px; }
        .lsp-track::-webkit-scrollbar { display:none; }
        .lsp-card { scroll-snap-align:start; flex:0 0 calc((100% - 28px)/3); height:186px; box-sizing:border-box; display:flex; flex-direction:column; background:var(--surface); border:1px solid var(--border); border-radius:var(--r-lg); padding:16px; text-decoration:none; transition:transform .2s, border-color .2s, box-shadow .2s; }
        .lsp-card:hover { transform:translateY(-3px); border-color:var(--border-hover); box-shadow:var(--shadow-sm); }
        @media (max-width:820px){ .lsp-card { flex:0 0 82%; } }
        .lsp-arrow { position:absolute; top:50%; transform:translateY(-50%); z-index:5; width:38px; height:38px; border-radius:50%; border:1px solid var(--border-strong); background:var(--surface); display:grid; place-items:center; cursor:pointer; box-shadow:var(--shadow-sm); transition:transform .2s, border-color .2s; }
        .lsp-arrow:hover { transform:translateY(-50%) scale(1.08); border-color:var(--brand); }
        @media (max-width:640px){ .lsp-arrow { display:none; } }
      `}</style>

      <div className="lsp-mono" style={{ fontSize: "0.65625rem", letterSpacing: ".28em", color: "var(--brand)" }}>Already on Sift</div>
      <h2 style={{ fontWeight: 700, fontSize: "clamp(1.5rem,2.8vw,2rem)", letterSpacing: "-.02em", margin: "10px 0 4px" }}>
        Real people and work, live right now.
      </h2>
      <p style={{ color: "var(--text-secondary)", fontSize: "0.9375rem", maxWidth: 480, margin: "0 auto 20px" }}>
        Not a promise. These are actual profiles and roles posted on the platform today.
      </p>

      <div
        style={{ position: "relative", maxWidth: 980, margin: "0 auto" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={() => setPaused(false)}
      >
        {cards.length > 3 && (
          <button type="button" className="lsp-arrow" style={{ left: -10 }} aria-label="Previous" onClick={() => nudge(-1)}>
            <ChevronLeft size={19} strokeWidth={2} color="var(--ink)" aria-hidden="true" />
          </button>
        )}
        <div className="lsp-track" ref={trackRef}>
          {cards}
        </div>
        {cards.length > 3 && (
          <button type="button" className="lsp-arrow" style={{ right: -10 }} aria-label="Next" onClick={() => nudge(1)}>
            <ChevronRight size={19} strokeWidth={2} color="var(--ink)" aria-hidden="true" />
          </button>
        )}
      </div>

      <Link to="/browse" className="lsp-mono" style={{ display: "inline-block", marginTop: 20, color: "var(--brand)", fontWeight: 600, fontSize: "0.6875rem", letterSpacing: ".14em" }}>
        Browse everyone on Sift →
      </Link>
    </section>
  );
}
